import React, {
  useEffect,
  useState,
  createContext,
  useContext,
} from 'react';

type ThemeContextType = {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_KEY = 'theme';

const readSavedTheme = (): boolean => {
  try {
    return localStorage.getItem(THEME_KEY) !== 'light';
  } catch {
    return true;
  }
};

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  // Dark is the default (#603). Only an explicit toggle is remembered, under
  // THEME_KEY. The old `darkMode` key cannot tell a choice from a default:
  // it was written on every mount, so every existing browser holds `false`
  // whether or not its user ever touched the toggle. It is ignored rather
  // than migrated for that reason. public/theme-init.js applies the same
  // rule before React mounts, so the first paint is already dark.
  const [isDarkMode, setIsDarkMode] = useState(readSavedTheme);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    try {
      localStorage.setItem(THEME_KEY, next ? 'dark' : 'light');
    } catch {
      // Storage blocked: the choice lasts for this page only.
    }
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook for using dark mode theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
